// Generic Deal Listing Page — the page template every "Create Website" click
// from the Deal Board starts from. Self-contained (own scoped CSS), brand-
// agnostic (no agent/org-specific branding baked in, unlike dreamxTemplate.js
// which is the DreamX demo site's own branded 7-page template), and built to
// sit alongside whatever site the agent already has rather than replace it.
//
// Class names are prefixed with dlp- (Deal Listing Page) and scoped under
// .dlp-root so they can't collide with dreamxTemplate.js or other templates'
// class names when this page's CSS is merged into the shared project
// stylesheet (GrapesJS's CssComposer is project-wide, not per-page).

function formatMoney(amount, isLease) {
  if (typeof amount !== 'number') return null;
  return `$${amount.toLocaleString()}${isLease ? '/mo' : ''}`;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}

// deal: { address, city, price, rent, beds, dealType }
export function buildDealListingPage(deal) {
  const isLease = deal.dealType === 'Lease';
  const price = formatMoney(isLease ? deal.rent : deal.price, isLease);
  const address = escapeHtml(deal.address) || 'New Listing';
  const city = escapeHtml(deal.city);
  const dealType = escapeHtml(deal.dealType) || 'For Sale';
  const beds = deal.beds ?? '—';

  const html = `
    <div class="dlp-root">
      <section class="dlp-hero">
        <div class="dlp-hero-photo" data-gjs-type="image">
          <div class="dlp-photo-placeholder">Click to add a hero photo</div>
        </div>
        <div class="dlp-hero-overlay">
          <span class="dlp-badge">${dealType}</span>
          <h1 class="dlp-title">${address}</h1>
          ${city ? `<p class="dlp-subtitle">${city}</p>` : ''}
          ${price ? `<div class="dlp-price">${price}</div>` : ''}
        </div>
      </section>

      <section class="dlp-details">
        <div class="dlp-container">
          <div class="dlp-grid">
            <div class="dlp-main">
              <div class="dlp-stats">
                <div class="dlp-stat">
                  <span class="dlp-stat-number">${beds}</span>
                  <span class="dlp-stat-label">Bedrooms</span>
                </div>
                <div class="dlp-stat">
                  <span class="dlp-stat-number">—</span>
                  <span class="dlp-stat-label">Bathrooms</span>
                </div>
                <div class="dlp-stat">
                  <span class="dlp-stat-number">—</span>
                  <span class="dlp-stat-label">Sq Ft</span>
                </div>
                <div class="dlp-stat">
                  <span class="dlp-stat-number">${dealType}</span>
                  <span class="dlp-stat-label">Status</span>
                </div>
              </div>

              <h2 class="dlp-section-title">About this property</h2>
              <p class="dlp-description">Add a description of this listing here — highlight the layout, recent updates, neighborhood, and anything that makes it stand out.</p>

              <h2 class="dlp-section-title">Photo Gallery</h2>
              <div class="dlp-gallery">
                <div class="dlp-gallery-item" data-gjs-type="image"><div class="dlp-photo-placeholder small">Add photo</div></div>
                <div class="dlp-gallery-item" data-gjs-type="image"><div class="dlp-photo-placeholder small">Add photo</div></div>
                <div class="dlp-gallery-item" data-gjs-type="image"><div class="dlp-photo-placeholder small">Add photo</div></div>
              </div>
            </div>

            <div class="dlp-sidebar">
              <div class="dlp-contact-card">
                <h3>Interested in this listing?</h3>
                <p>Contact us to schedule a showing or ask a question.</p>
                <a href="tel:" class="dlp-btn dlp-btn-primary">Call agent</a>
                <a href="mailto:" class="dlp-btn dlp-btn-secondary">Email agent</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  `;

  return { html, css: DEAL_LISTING_CSS };
}

const DEAL_LISTING_CSS = `
.dlp-root { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1d1d1f; background: #fff; }
.dlp-container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

.dlp-hero { position: relative; background: #111; }
.dlp-hero-photo { position: relative; height: 62vh; min-height: 420px; overflow: hidden; }
.dlp-photo-placeholder { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg,#2b2b30,#17171a); color: #8b8b93; font-size: 15px; text-align: center; padding: 20px; cursor: pointer; }
.dlp-photo-placeholder.small { font-size: 12px; }
.dlp-hero-overlay { position: absolute; left: 0; right: 0; bottom: 0; padding: 48px 24px; background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 85%); color: #fff; max-width: 1160px; margin: 0 auto; }
.dlp-badge { display: inline-block; background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.3); padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; margin-bottom: 14px; }
.dlp-title { font-size: 2.75rem; font-weight: 700; margin: 0 0 6px; line-height: 1.1; }
.dlp-subtitle { font-size: 1.05rem; opacity: 0.85; margin: 0 0 16px; }
.dlp-price { display: inline-block; background: #007aff; color: #fff; padding: 10px 20px; border-radius: 999px; font-size: 1.15rem; font-weight: 700; }

.dlp-details { padding: 56px 0 80px; background: #f5f5f7; }
.dlp-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 36px; }
.dlp-main { background: #fff; border-radius: 16px; padding: 36px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
.dlp-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; padding-bottom: 26px; margin-bottom: 26px; border-bottom: 1px solid #e5e5e7; }
.dlp-stat { text-align: center; }
.dlp-stat-number { display: block; font-size: 1.6rem; font-weight: 700; color: #007aff; margin-bottom: 4px; }
.dlp-stat-label { font-size: 0.8rem; color: #86868b; text-transform: uppercase; letter-spacing: 0.04em; }
.dlp-section-title { font-size: 1.3rem; font-weight: 700; margin: 0 0 14px; }
.dlp-description { font-size: 1rem; line-height: 1.65; color: #3a3a3c; margin: 0 0 32px; }
.dlp-gallery { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.dlp-gallery-item { position: relative; aspect-ratio: 4/3; border-radius: 10px; overflow: hidden; }

.dlp-sidebar { display: flex; flex-direction: column; gap: 20px; }
.dlp-contact-card { background: #fff; border-radius: 16px; padding: 28px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
.dlp-contact-card h3 { margin: 0 0 8px; font-size: 1.1rem; }
.dlp-contact-card p { margin: 0 0 20px; color: #86868b; font-size: 0.9rem; }
.dlp-btn { display: block; text-align: center; padding: 12px 18px; border-radius: 8px; font-weight: 600; font-size: 0.92rem; margin-bottom: 10px; }
.dlp-btn-primary { background: #007aff; color: #fff; }
.dlp-btn-secondary { background: #f0f0f2; color: #1d1d1f; }

@media (max-width: 820px) {
  .dlp-grid { grid-template-columns: 1fr; }
  .dlp-stats { grid-template-columns: repeat(2, 1fr); }
  .dlp-title { font-size: 2rem; }
}
`;
